import { useMemo, useState } from "react";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../Pagination/PageSizeControls";
import type { PaginationProps } from "../../Pagination/types";
import type { TablePagination } from "../types";

type Props<Item> = {
  items: Item[];
  pagination?: boolean | TablePagination;
};

type UsePaginationControllerReturn<Item> = {
  paginatedItems: Item[];
  paginationConfig: PaginationProps | undefined;
  resetPage: () => void;
};

export function usePaginationController<Item>({
  items,
  pagination,
}: Props<Item>): UsePaginationControllerReturn<Item> {
  const [internalPage, setInternalPage] = useState(1);
  const [internalPageSize, setInternalPageSize] = useState(
    DEFAULT_PAGE_SIZE_OPTIONS[0],
  );

  return useMemo(() => {
    if (!pagination) {
      return {
        paginatedItems: items,
        paginationConfig: undefined,
        resetPage: () => {},
      };
    }

    if (pagination === true) {
      const startIndex = (internalPage - 1) * internalPageSize;
      const endIndex = startIndex + internalPageSize;
      const slicedItems = items.slice(startIndex, endIndex);

      const config: PaginationProps = {
        page: internalPage,
        pageSize: internalPageSize,
        totalItems: items.length,
        onPageChange: setInternalPage,
        onPageSizeChange: (newPageSize: number) => {
          setInternalPageSize(newPageSize);
          setInternalPage(1);
        },
      };

      return {
        paginatedItems: slicedItems,
        paginationConfig: config,
        resetPage: () => setInternalPage(1),
      };
    }

    const shouldSlice = !pagination.server;
    if (shouldSlice) {
      const startIndex = (pagination.page - 1) * pagination.pageSize;
      const endIndex = startIndex + pagination.pageSize;
      const slicedItems = items.slice(startIndex, endIndex);

      const { server, ...paginationProps } = pagination;

      return {
        paginatedItems: slicedItems,
        paginationConfig: paginationProps,
        resetPage: () => {},
      };
    }

    const { server, ...paginationProps } = pagination;

    return {
      paginatedItems: items,
      paginationConfig: paginationProps,
      resetPage: () => {},
    };
  }, [items, pagination, internalPage, internalPageSize]);
}
